import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/api';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Loader2, Plus, Trash2, ArrowLeft, GripVertical, Pencil, X, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import useRepairCategories from '@/hooks/useRepairCategories';

export default function RepairCategoriesAdmin() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = ['master_admin', 'admin'].includes(user?.role);

  const { reload } = useRepairCategories();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInactive, setShowInactive] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => { if (isAdmin) load(); }, [isAdmin, showInactive]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/repair-event-categories', { params: showInactive ? { include_inactive: true } : {} });
      setItems(data.items || []);
    } catch {
      toast.error('Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    const name = newName.trim();
    if (!name) return;
    setAdding(true);
    try {
      await api.post('/repair-event-categories', { name });
      toast.success(`Added "${name}"`);
      setNewName('');
      reload();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add category');
    } finally {
      setAdding(false);
    }
  };

  const startEdit = (cat) => { setEditingId(cat.id); setEditName(cat.name); };
  const cancelEdit = () => { setEditingId(null); setEditName(''); };

  const saveEdit = async (cat) => {
    const name = editName.trim();
    if (!name || name === cat.name) { cancelEdit(); return; }
    setBusyId(cat.id);
    try {
      await api.put(`/repair-event-categories/${cat.id}`, { name });
      toast.success('Renamed');
      cancelEdit();
      reload();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to rename');
    } finally {
      setBusyId(null);
    }
  };

  const toggleActive = async (cat) => {
    setBusyId(cat.id);
    try {
      await api.put(`/repair-event-categories/${cat.id}`, { is_active: !cat.is_active });
      toast.success(cat.is_active ? 'Archived' : 'Restored');
      reload();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update');
    } finally {
      setBusyId(null);
    }
  };

  const move = async (cat, direction) => {
    // Swap sort_order with the neighbour in the displayed list
    const idx = items.findIndex(c => c.id === cat.id);
    const targetIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (targetIdx < 0 || targetIdx >= items.length) return;
    const target = items[targetIdx];
    setBusyId(cat.id);
    try {
      await Promise.all([
        api.put(`/repair-event-categories/${cat.id}`, { sort_order: target.sort_order }),
        api.put(`/repair-event-categories/${target.id}`, { sort_order: cat.sort_order }),
      ]);
      reload();
      load();
    } catch (e) {
      toast.error('Failed to reorder');
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setBusyId(deleteTarget.id);
    try {
      await api.delete(`/repair-event-categories/${deleteTarget.id}`);
      toast.success(`Archived "${deleteTarget.name}"`);
      setDeleteTarget(null);
      reload();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to archive');
    } finally {
      setBusyId(null);
    }
  };

  if (!isAdmin) {
    return (
      <div className="p-8" data-testid="categories-admin-403">
        <p style={{ color: 'var(--error)' }}>Admin access required.</p>
      </div>
    );
  }

  return (
    <div className="space-y-5 animate-fade-in max-w-3xl" data-testid="repair-categories-admin">
      <div className="flex items-center justify-between">
        <div>
          <button onClick={() => navigate('/ops/settings')} className="text-xs font-['JetBrains_Mono'] inline-flex items-center gap-1 mb-2 hover:underline" style={{ color: 'var(--text-muted)' }}>
            <ArrowLeft size={12} /> Back to Settings
          </button>
          <h1 className="font-['Manrope'] font-bold text-2xl" style={{ color: 'var(--text-primary)' }}>Repair Event Categories</h1>
          <p className="font-['IBM_Plex_Sans'] text-sm mt-0.5" style={{ color: 'var(--text-muted)' }}>
            These show up in the New Repair modal. Archive instead of delete so old repairs keep their label.
          </p>
        </div>
        <label className="flex items-center gap-2 text-xs font-['JetBrains_Mono']" style={{ color: 'var(--text-muted)' }}>
          <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} data-testid="show-archived-toggle" />
          Show archived
        </label>
      </div>

      {/* Add form */}
      <form onSubmit={handleAdd} className="card-slate p-3 flex items-center gap-2">
        <Plus size={14} style={{ color: 'var(--primary)' }} />
        <input
          type="text"
          value={newName}
          onChange={e => setNewName(e.target.value)}
          placeholder="New category name (e.g. Reefer/Refrigeration)"
          className="flex-1 input-slate"
          data-testid="new-category-input"
          maxLength={60}
        />
        <Button type="submit" disabled={adding || !newName.trim()} data-testid="add-category-btn" className="btn-primary text-xs uppercase">
          {adding ? <Loader2 size={14} className="animate-spin" /> : 'Add'}
        </Button>
      </form>

      {/* List */}
      <div className="card-slate overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="animate-spin" size={20} style={{ color: 'var(--primary)' }} /></div>
        ) : items.length === 0 ? (
          <p className="text-center py-12 text-sm font-['IBM_Plex_Sans']" style={{ color: 'var(--text-muted)' }}>No categories yet</p>
        ) : (
          <table className="w-full table-comfortable" data-testid="categories-table">
            <thead>
              <tr>
                <th className="w-8"></th>
                <th>Name</th>
                <th className="w-24 text-center hidden sm:table-cell">Status</th>
                <th className="w-32 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((cat, idx) => {
                const editing = editingId === cat.id;
                const inactive = !cat.is_active;
                return (
                  <tr key={cat.id} data-testid={`category-row-${cat.id}`} style={{ opacity: inactive ? 0.5 : 1 }}>
                    <td>
                      <div className="flex flex-col items-center gap-0.5">
                        <button onClick={() => move(cat, 'up')} disabled={idx === 0 || busyId === cat.id}
                          className="text-[10px] leading-none px-1 disabled:opacity-30 hover:text-[var(--primary)]"
                          title="Move up" data-testid={`move-up-${cat.id}`}>▲</button>
                        <button onClick={() => move(cat, 'down')} disabled={idx === items.length - 1 || busyId === cat.id}
                          className="text-[10px] leading-none px-1 disabled:opacity-30 hover:text-[var(--primary)]"
                          title="Move down" data-testid={`move-down-${cat.id}`}>▼</button>
                      </div>
                    </td>
                    <td>
                      {editing ? (
                        <input
                          type="text"
                          value={editName}
                          onChange={e => setEditName(e.target.value)}
                          autoFocus
                          onKeyDown={e => { if (e.key === 'Enter') saveEdit(cat); if (e.key === 'Escape') cancelEdit(); }}
                          className="w-full input-slate"
                          data-testid={`edit-input-${cat.id}`}
                        />
                      ) : (
                        <span className="font-['IBM_Plex_Sans']" style={{ color: 'var(--text-primary)' }}>{cat.name}</span>
                      )}
                    </td>
                    <td className="hidden sm:table-cell text-center">
                      {inactive ? (
                        <span className="text-[9px] font-['JetBrains_Mono'] uppercase px-2 py-0.5 rounded-full border" style={{ color: 'var(--text-faint)', borderColor: 'var(--border)' }}>archived</span>
                      ) : (
                        <span className="text-[9px] font-['JetBrains_Mono'] uppercase px-2 py-0.5 rounded-full" style={{ color: 'var(--success)', background: 'var(--success-muted)' }}>active</span>
                      )}
                    </td>
                    <td className="text-right">
                      <div className="inline-flex items-center gap-1">
                        {editing ? (
                          <>
                            <button onClick={() => saveEdit(cat)} disabled={busyId === cat.id}
                              data-testid={`save-edit-${cat.id}`}
                              className="p-1.5 rounded hover:bg-[var(--success-muted)]" title="Save">
                              <Check size={14} style={{ color: 'var(--success)' }} />
                            </button>
                            <button onClick={cancelEdit} className="p-1.5 rounded hover:bg-[var(--bg-darker)]" title="Cancel">
                              <X size={14} style={{ color: 'var(--text-muted)' }} />
                            </button>
                          </>
                        ) : (
                          <>
                            <button onClick={() => startEdit(cat)} disabled={busyId === cat.id || inactive}
                              data-testid={`edit-cat-${cat.id}`}
                              className="p-1.5 rounded hover:bg-[var(--bg-darker)] disabled:opacity-30" title="Rename">
                              <Pencil size={13} style={{ color: 'var(--text-muted)' }} />
                            </button>
                            <button onClick={() => toggleActive(cat)} disabled={busyId === cat.id}
                              data-testid={`toggle-active-${cat.id}`}
                              className="text-[10px] font-['JetBrains_Mono'] uppercase px-2 py-1 rounded hover:bg-[var(--bg-darker)]"
                              style={{ color: 'var(--text-muted)' }}
                              title={inactive ? 'Restore' : 'Archive'}>
                              {inactive ? 'restore' : 'archive'}
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Confirm delete (currently unused — we soft-archive instead, kept for future hard delete) */}
      <Dialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <DialogContent style={{ background: 'var(--surface)', borderColor: 'var(--border)' }} className="max-w-sm">
          <DialogHeader>
            <DialogTitle style={{ color: 'var(--error)' }} className="font-['Manrope']">Archive Category</DialogTitle>
          </DialogHeader>
          <p className="text-sm font-['IBM_Plex_Sans'] mt-2" style={{ color: 'var(--text-muted)' }}>
            Archive <span className="font-bold" style={{ color: 'var(--text-primary)' }}>{deleteTarget?.name}</span>?
            Existing repairs keep this label, but it will no longer appear in dropdowns.
          </p>
          <DialogFooter className="mt-4 gap-2">
            <Button variant="outline" onClick={() => setDeleteTarget(null)} style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }}>Cancel</Button>
            <Button onClick={handleDelete} style={{ background: 'var(--error)', color: 'white' }}>
              <Trash2 size={14} className="mr-2" />Archive
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
